import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { 
  ArrowLeft, 
  ArrowRight, 
  ChevronRight,
  Utensils, 
  Zap, 
  ShoppingBasket, 
  Croissant, 
  Beef, 
  Wine, 
  Fish, 
  MilkOff,
  Check,
  MapPin,
  Clock,
  CreditCard,
  User,
  FileText,
  CheckCircle2,
  Info,
  Upload,
  Phone,
  Mail,
  Globe,
  Building2,
  Calendar,
  MessageCircle,
  Video
} from 'lucide-react';

// Configuration des types de commerce
const SHOP_TYPES = [
  { id: 'restaurant_traditionnel', label: 'Restaurant traditionnel', icon: Utensils, color: 'bg-orange-100 text-orange-600' },
  { id: 'restaurant_rapide', label: 'Restaurant rapide', icon: Zap, color: 'bg-yellow-100 text-yellow-600' },
  { id: 'epicerie', label: 'Épicerie', icon: ShoppingBasket, color: 'bg-green-100 text-green-600' },
  { id: 'boulangerie', label: 'Boulangerie', icon: Croissant, color: 'bg-amber-100 text-amber-600' },
  { id: 'boucherie', label: 'Boucherie', icon: Beef, color: 'bg-red-100 text-red-600' },
  { id: 'epicerie_fine', label: 'Épicerie fine', icon: Wine, color: 'bg-purple-100 text-purple-600' },
  { id: 'poissonnier', label: 'Poissonnier', icon: Fish, color: 'bg-blue-100 text-blue-600' },
  { id: 'fromager', label: 'Fromager', icon: MilkOff, color: 'bg-indigo-100 text-indigo-600' },
];

// Configuration des étapes du wizard
const WIZARD_STEPS = [
  { id: 'shop_type', label: 'Type', icon: Building2 },
  { id: 'quick_start', label: 'Démarrage', icon: Zap },
  { id: 'shop_details', label: 'Détails', icon: FileText },
  { id: 'basic_info', label: 'Infos', icon: MapPin },
  { id: 'operations', label: 'Opérations', icon: Clock },
  { id: 'account', label: 'Compte', icon: User },
  { id: 'documents', label: 'Documents', icon: FileText },
  { id: 'confirmation', label: 'Confirmation', icon: CheckCircle2 },
];

// Jours de la semaine
const DAYS = [
  { id: 'monday', label: 'Lundi' },
  { id: 'tuesday', label: 'Mardi' },
  { id: 'wednesday', label: 'Mercredi' },
  { id: 'thursday', label: 'Jeudi' },
  { id: 'friday', label: 'Vendredi' },
  { id: 'saturday', label: 'Samedi' },
  { id: 'sunday', label: 'Dimanche' },
];

// Moyens de paiement
const PAYMENT_METHODS = [
  { id: 'card', label: 'Carte bancaire', icon: CreditCard },
  { id: 'cash', label: 'Espèces', icon: '💵' },
  { id: 'ticket_resto_paper', label: 'Ticket restaurant papier', icon: '🎫' },
  { id: 'ticket_resto_card', label: 'Carte ticket restaurant', icon: '💳' },
  { id: 'amex', label: 'American Express', icon: '💎' },
  { id: 'bitcoin', label: 'Bitcoin', icon: '₿' },
];

// Questions conditionnelles par type de commerce
const CONDITIONAL_QUESTIONS = {
  restaurant_traditionnel: [
    { id: 'cuisine_type', label: 'Type de cuisine', type: 'select', required: true, options: ['Française', 'Italienne', 'Asiatique', 'Méditerranéenne', 'Autre'] },
    { id: 'sells_alcohol', label: 'Vente d\'alcool', type: 'boolean', required: true },
  ],
  restaurant_rapide: [
    { id: 'prep_time', label: 'Temps de préparation moyen', type: 'select', required: true, options: ['5 min', '10 min', '15 min', '20 min+'] },
    { id: 'sells_alcohol', label: 'Vente d\'alcool', type: 'boolean', required: true },
  ],
  epicerie: [
    { id: 'avg_basket', label: 'Panier moyen estimé', type: 'select', required: true, options: ['Moins de 20€', '20€ - 40€', '40€ - 60€', '60€+'] },
    { id: 'sells_alcohol', label: 'Vente d\'alcool', type: 'boolean', required: true },
    { id: 'night_opening', label: 'Ouverture de nuit', type: 'boolean', required: true },
  ],
  boulangerie: [
    { id: 'opening_hour', label: 'Heure d\'ouverture habituelle', type: 'select', required: true, options: ['5h', '6h', '7h', '8h'] },
    { id: 'makes_on_site', label: 'Fabrique sur place', type: 'boolean', required: true },
    { id: 'also_sells', label: 'Vend aussi', type: 'multiselect', required: false, options: ['Café', 'Pâtisseries', 'Sandwichs', 'Viennoiseries'] },
    { id: 'sells_alcohol', label: 'Vente d\'alcool', type: 'boolean', required: true },
  ],
  boucherie: [
    { id: 'processed_products', label: 'Produits transformés', type: 'multiselect', required: false, options: ['Saucisses', 'Merguez', 'Brochettes', 'Plats préparés'] },
    { id: 'open_sunday', label: 'Ouvert le dimanche', type: 'boolean', required: true },
    { id: 'sells_alcohol', label: 'Vente d\'alcool', type: 'boolean', required: true },
  ],
  fromager: [
    { id: 'dairy_products', label: 'Produits laitiers complémentaires', type: 'multiselect', required: false, options: ['Beurre', 'Crème', 'Yaourts', 'Lait'] },
    { id: 'sells_alcohol', label: 'Vente d\'alcool', type: 'boolean', required: true },
  ],
  poissonnier: [
    { id: 'refrigerated_package', label: 'Colis réfrigéré disponible', type: 'boolean', required: true },
    { id: 'on_site_prep', label: 'Préparation sur place', type: 'multiselect', required: false, options: ['Filetage', 'Écaillage', 'Découpe'] },
    { id: 'sells_frozen_smoked', label: 'Vente fumé / congelé', type: 'boolean', required: true },
    { id: 'sells_alcohol', label: 'Vente d\'alcool', type: 'boolean', required: true },
  ],
  epicerie_fine: [
    { id: 'specialties', label: 'Type de spécialités', type: 'multiselect', required: true, options: ['Vins', 'Épicerie du monde', 'Caviar', 'Truffes', 'Huiles & vinaigres'] },
    { id: 'sells_cheese_charcuterie', label: 'Vente fromages / charcuterie', type: 'boolean', required: true },
    { id: 'sells_alcohol', label: 'Vente d\'alcool', type: 'boolean', required: true },
  ],
};

export default function WizardPreview() {
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedShopType, setSelectedShopType] = useState(null);
  const [sameHoursAllWeek, setSameHoursAllWeek] = useState(false);
  const [selectedPayments, setSelectedPayments] = useState(['card']);
  const [closedDays, setClosedDays] = useState([]);
  const [shopTypeAnswers, setShopTypeAnswers] = useState({});

  const progress = ((currentStep + 1) / WIZARD_STEPS.length) * 100;

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return <ShopTypeStep selectedShopType={selectedShopType} setSelectedShopType={setSelectedShopType} />;
      case 1:
        return <QuickStartStep />;
      case 2:
        return <ShopDetailsStep shopType={selectedShopType} answers={shopTypeAnswers} setAnswers={setShopTypeAnswers} />;
      case 3:
        return <BasicInfoStep />;
      case 4:
        return <OperationsStep 
          sameHoursAllWeek={sameHoursAllWeek} 
          setSameHoursAllWeek={setSameHoursAllWeek}
          selectedPayments={selectedPayments}
          setSelectedPayments={setSelectedPayments}
          closedDays={closedDays}
          setClosedDays={setClosedDays}
        />;
      case 5:
        return <AccountStep />;
      case 6:
        return <DocumentsStep />;
      case 7:
        return <ConfirmationStep shopType={selectedShopType} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="wizard-preview">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2" data-testid="wizard-logo">
              <img src="/logo.png" alt="LEMO" className="w-20 h-20 -my-4 object-contain" />
              <span className="font-bold text-xl text-slate-900">LEMO</span>
            </Link>
            <Link 
              to="/" 
              className="text-slate-500 hover:text-slate-700 flex items-center gap-2 text-sm"
              data-testid="wizard-back-link"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Retour</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="bg-white border-b border-slate-100">
        <div className="max-w-2xl mx-auto px-4 py-4">
          {/* Breadcrumbs */}
          <div className="flex items-center gap-2 text-sm text-slate-500 mb-3 overflow-x-auto pb-1">
            {WIZARD_STEPS.map((step, index) => (
              <div key={step.id} className="flex items-center gap-2 whitespace-nowrap">
                <span 
                  className={`flex items-center gap-1.5 ${
                    index === currentStep 
                      ? 'text-emerald-600 font-medium' 
                      : index < currentStep 
                        ? 'text-emerald-500' 
                        : 'text-slate-400'
                  }`}
                >
                  {index < currentStep ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs ${
                      index === currentStep 
                        ? 'bg-emerald-500 text-white' 
                        : 'bg-slate-200 text-slate-500'
                    }`}>
                      {index + 1}
                    </span>
                  )}
                  <span className="hidden sm:inline">{step.label}</span>
                </span>
                {index < WIZARD_STEPS.length - 1 && (
                  <ChevronRight className="w-4 h-4 text-slate-300" />
                )}
              </div>
            ))}
          </div>
          
          {/* Progress Bar */}
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div 
              className="h-full bg-emerald-500 transition-all duration-500 ease-out rounded-full"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-xs text-slate-500 mt-2 text-right">{Math.round(progress)}% complété</p>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 py-6 md:py-10">
        <div className="max-w-2xl mx-auto px-4">
          {renderStepContent()}
        </div>
      </main>

      {/* Action Bar (Sticky on mobile) */}
      <div className="sticky bottom-0 bg-white border-t border-slate-100 p-4 md:static md:bg-transparent md:border-0">
        <div className="max-w-2xl mx-auto flex items-center justify-between gap-4">
          <Button
            variant="outline"
            onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
            disabled={currentStep === 0}
            className="h-12 px-6 rounded-full"
            data-testid="wizard-prev-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Précédent
          </Button>
          
          {currentStep < WIZARD_STEPS.length - 1 ? (
            <Button
              onClick={() => setCurrentStep(Math.min(WIZARD_STEPS.length - 1, currentStep + 1))}
              className="h-12 px-8 bg-emerald-500 hover:bg-emerald-600 rounded-full flex-1 md:flex-none"
              data-testid="wizard-next-btn"
            >
              Continuer
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              className="h-12 px-8 bg-emerald-500 hover:bg-emerald-600 rounded-full flex-1 md:flex-none"
              data-testid="wizard-submit-btn"
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Finaliser l'inscription
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// === ÉTAPE 1: Sélection du type de commerce ===
function ShopTypeStep({ selectedShopType, setSelectedShopType }) {
  return (
    <div className="space-y-6" data-testid="step-shop-type">
      <div className="text-center">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
          Quel type de commerce ?
        </h1>
        <p className="text-slate-500">
          Sélectionnez le type qui correspond le mieux à votre activité
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        {SHOP_TYPES.map((type) => {
          const Icon = type.icon;
          const isSelected = selectedShopType === type.id;
          return (
            <button
              key={type.id}
              onClick={() => setSelectedShopType(type.id)}
              className={`flex flex-col items-center justify-center p-4 md:p-6 border-2 rounded-xl cursor-pointer transition-all ${
                isSelected 
                  ? 'border-emerald-500 bg-emerald-50 ring-1 ring-emerald-500' 
                  : 'border-slate-200 bg-white hover:border-emerald-200 hover:bg-emerald-50/50'
              }`}
              data-testid={`shop-type-${type.id}`}
            >
              <div className={`w-12 h-12 md:w-14 md:h-14 rounded-xl ${type.color} flex items-center justify-center mb-3`}>
                <Icon className="w-6 h-6 md:w-7 md:h-7" />
              </div>
              <span className={`text-sm md:text-base font-medium text-center ${isSelected ? 'text-emerald-700' : 'text-slate-700'}`}>
                {type.label}
              </span>
              {isSelected && (
                <div className="mt-2 w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center">
                  <Check className="w-3 h-3 text-white" />
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// === ÉTAPE 2: Démarrage rapide (Mini-formulaire) ===
function QuickStartStep() {
  return (
    <div className="space-y-6" data-testid="step-quick-start">
      <div className="text-center">
        <div className="w-16 h-16 rounded-2xl bg-emerald-100 flex items-center justify-center mx-auto mb-4">
          <Zap className="w-8 h-8 text-emerald-600" />
        </div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
          Démarrage rapide
        </h1>
        <p className="text-slate-500">
          Cela prend 2 minutes. Vous pourrez compléter les détails plus tard.
        </p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-6 space-y-5">
        <div>
          <Label htmlFor="shop_name" className="text-sm font-medium text-slate-700">
            Nom du commerce <span className="text-red-500">*</span>
          </Label>
          <Input
            id="shop_name"
            type="text"
            placeholder="Ex: Boulangerie du Marché"
            className="mt-1.5 h-12 rounded-xl"
            data-testid="input-shop-name"
          />
        </div>

        <div>
          <Label htmlFor="address" className="text-sm font-medium text-slate-700">
            Adresse <span className="text-red-500">*</span>
          </Label>
          <div className="relative mt-1.5">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              id="address"
              type="text"
              placeholder="Commencez à taper votre adresse..."
              className="h-12 rounded-xl pl-10"
              data-testid="input-address"
            />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="contact_name" className="text-sm font-medium text-slate-700">
              Prénom du contact <span className="text-red-500">*</span>
            </Label>
            <Input
              id="contact_name"
              type="text"
              placeholder="Jean"
              className="mt-1.5 h-12 rounded-xl"
              data-testid="input-contact-name"
            />
          </div>
          <div>
            <Label htmlFor="contact_email" className="text-sm font-medium text-slate-700">
              Email <span className="text-red-500">*</span>
            </Label>
            <Input
              id="contact_email"
              type="email"
              placeholder="jean@example.com"
              className="mt-1.5 h-12 rounded-xl"
              data-testid="input-contact-email"
            />
          </div>
        </div>
      </div>

      <div className="bg-emerald-50 rounded-xl p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-emerald-700">
          Ces informations nous permettent de créer votre espace. Vous pourrez modifier tous les détails ensuite.
        </p>
      </div>
    </div>
  );
}

// === ÉTAPE 3: Questions conditionnelles selon le type de commerce ===
function ShopDetailsStep({ shopType, answers, setAnswers }) {
  const questions = CONDITIONAL_QUESTIONS[shopType] || [];
  const shopTypeLabel = SHOP_TYPES.find(t => t.id === shopType)?.label || 'Commerce';

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleMultiSelectToggle = (questionId, option) => {
    const current = answers[questionId] || [];
    if (current.includes(option)) {
      setAnswers(prev => ({ ...prev, [questionId]: current.filter(o => o !== option) }));
    } else {
      setAnswers(prev => ({ ...prev, [questionId]: [...current, option] }));
    }
  };

  if (questions.length === 0) {
    return (
      <div className="space-y-6 text-center" data-testid="step-shop-details">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900">
          Parfait !
        </h1>
        <p className="text-slate-500">
          Pas de questions supplémentaires pour ce type de commerce.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="step-shop-details">
      <div className="text-center">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
          Détails {shopTypeLabel}
        </h1>
        <p className="text-slate-500">
          Quelques questions spécifiques à votre activité
        </p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-6 space-y-6">
        {questions.map((question) => (
          <div key={question.id} className="space-y-2">
            <Label className="text-sm font-medium text-slate-700">
              {question.label}
              {question.required && <span className="text-red-500 ml-1">*</span>}
            </Label>

            {/* Select type */}
            {question.type === 'select' && (
              <select
                value={answers[question.id] || ''}
                onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                className="w-full h-12 rounded-xl border border-slate-200 px-4 bg-white focus:border-emerald-500 focus:ring-emerald-500"
                data-testid={`select-${question.id}`}
              >
                <option value="">Sélectionnez une option</option>
                {question.options.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            )}

            {/* Boolean type */}
            {question.type === 'boolean' && (
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => handleAnswerChange(question.id, true)}
                  className={`flex-1 h-12 rounded-xl border-2 font-medium transition-all ${
                    answers[question.id] === true
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                      : 'border-slate-200 text-slate-600 hover:border-slate-300'
                  }`}
                  data-testid={`btn-${question.id}-yes`}
                >
                  Oui
                </button>
                <button
                  type="button"
                  onClick={() => handleAnswerChange(question.id, false)}
                  className={`flex-1 h-12 rounded-xl border-2 font-medium transition-all ${
                    answers[question.id] === false
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                      : 'border-slate-200 text-slate-600 hover:border-slate-300'
                  }`}
                  data-testid={`btn-${question.id}-no`}
                >
                  Non
                </button>
              </div>
            )}

            {/* Multiselect type */}
            {question.type === 'multiselect' && (
              <div className="flex flex-wrap gap-2">
                {question.options.map((opt) => {
                  const isSelected = (answers[question.id] || []).includes(opt);
                  return (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => handleMultiSelectToggle(question.id, opt)}
                      className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                        isSelected
                          ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                          : 'border-slate-200 text-slate-600 hover:border-slate-300'
                      }`}
                      data-testid={`btn-${question.id}-${opt.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      {isSelected && <Check className="w-3 h-3 inline mr-1" />}
                      {opt}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Info box pour licence alcool */}
      {questions.some(q => q.id === 'sells_alcohol') && answers['sells_alcohol'] === true && (
        <div className="bg-amber-50 rounded-xl p-4 flex items-start gap-3">
          <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-700">
            La vente d'alcool nécessite une licence. Vous pourrez télécharger votre licence à l'étape Documents.
          </p>
        </div>
      )}
    </div>
  );
}

// === ÉTAPE 4: Informations de base ===
function BasicInfoStep() {
  return (
    <div className="space-y-6" data-testid="step-basic-info">
      <div className="text-center">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
          Informations de base
        </h1>
        <p className="text-slate-500">
          Renseignez les informations essentielles de votre commerce
        </p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-6 space-y-5">
        {/* Nom du commerce */}
        <div>
          <Label className="text-sm font-medium text-slate-700">
            Nom du commerce <span className="text-red-500">*</span>
          </Label>
          <Input
            type="text"
            placeholder="Boulangerie du Marché"
            className="mt-1.5 h-12 rounded-xl"
            data-testid="input-business-name"
          />
        </div>

        {/* Adresse complète */}
        <div className="space-y-4">
          <Label className="text-sm font-medium text-slate-700">
            Adresse complète <span className="text-red-500">*</span>
          </Label>
          <div className="relative">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              type="text"
              placeholder="Rue, numéro..."
              className="h-12 rounded-xl pl-10"
              data-testid="input-street"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input
              type="text"
              placeholder="Code postal"
              className="h-12 rounded-xl"
              data-testid="input-zipcode"
            />
            <Input
              type="text"
              placeholder="Ville"
              className="h-12 rounded-xl"
              data-testid="input-city"
            />
          </div>
          <Button variant="outline" className="w-full h-10 rounded-lg text-sm" data-testid="btn-geolocate">
            <MapPin className="w-4 h-4 mr-2" />
            Géolocaliser automatiquement
          </Button>
        </div>

        {/* Téléphone */}
        <div>
          <Label className="text-sm font-medium text-slate-700">
            Téléphone du commerce <span className="text-red-500">*</span>
          </Label>
          <div className="relative mt-1.5">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              type="tel"
              placeholder="+33 1 23 45 67 89"
              className="h-12 rounded-xl pl-10"
              data-testid="input-phone"
            />
          </div>
        </div>

        {/* Email */}
        <div>
          <Label className="text-sm font-medium text-slate-700">
            Email du commerce <span className="text-red-500">*</span>
          </Label>
          <div className="relative mt-1.5">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              type="email"
              placeholder="contact@votrecommerce.fr"
              className="h-12 rounded-xl pl-10"
              data-testid="input-email"
            />
          </div>
        </div>

        {/* Champs optionnels */}
        <div className="border-t border-slate-100 pt-5 space-y-4">
          <p className="text-sm text-slate-500 flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-slate-300"></span>
            Champs optionnels
          </p>

          {/* Logo */}
          <div>
            <Label className="text-sm font-medium text-slate-700">
              Logo / Image de couverture
              <span className="text-slate-400 font-normal ml-2">(optionnel)</span>
            </Label>
            <div className="mt-1.5 border-2 border-dashed border-slate-200 rounded-xl p-6 text-center hover:border-emerald-300 transition-colors cursor-pointer" data-testid="upload-logo">
              <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
              <p className="text-sm text-slate-600">Cliquez pour télécharger</p>
              <p className="text-xs text-slate-400 mt-1">PNG, JPG jusqu'à 5 Mo</p>
            </div>
          </div>

          {/* Site web */}
          <div>
            <Label className="text-sm font-medium text-slate-700">
              Site web
              <span className="text-slate-400 font-normal ml-2">(optionnel)</span>
            </Label>
            <div className="relative mt-1.5">
              <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type="url"
                placeholder="https://www.votresite.fr"
                className="h-12 rounded-xl pl-10"
                data-testid="input-website"
              />
            </div>
          </div>

          {/* SIRET */}
          <div>
            <div className="flex items-center gap-2">
              <Label className="text-sm font-medium text-slate-700">
                SIRET / SIREN
                <span className="text-slate-400 font-normal ml-2">(optionnel)</span>
              </Label>
              <div className="group relative">
                <Info className="w-4 h-4 text-slate-400 cursor-help" />
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-slate-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                  Le numéro SIRET permet de vérifier l'existence légale de votre entreprise. Il n'est pas obligatoire pour commencer.
                </div>
              </div>
            </div>
            <Input
              type="text"
              placeholder="123 456 789 00012"
              className="mt-1.5 h-12 rounded-xl"
              data-testid="input-siret"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// === ÉTAPE 5: Détails opérationnels ===
function OperationsStep({ sameHoursAllWeek, setSameHoursAllWeek, selectedPayments, setSelectedPayments, closedDays, setClosedDays }) {
  const togglePayment = (id) => {
    if (selectedPayments.includes(id)) {
      if (selectedPayments.length > 1) {
        setSelectedPayments(selectedPayments.filter(p => p !== id));
      }
    } else {
      setSelectedPayments([...selectedPayments, id]);
    }
  };

  const toggleClosedDay = (dayId) => {
    if (closedDays.includes(dayId)) {
      setClosedDays(closedDays.filter(d => d !== dayId));
    } else {
      setClosedDays([...closedDays, dayId]);
    }
  };

  return (
    <div className="space-y-6" data-testid="step-operations">
      <div className="text-center">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
          Détails opérationnels
        </h1>
        <p className="text-slate-500">
          Configurez vos horaires et moyens de paiement
        </p>
      </div>

      {/* Horaires d'ouverture */}
      <div className="bg-white rounded-2xl border border-slate-100 p-6 space-y-5">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Clock className="w-5 h-5 text-emerald-500" />
            Horaires d'ouverture
          </h2>
        </div>

        {/* Toggle mêmes horaires */}
        <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
          <Label htmlFor="same-hours" className="text-sm text-slate-600 cursor-pointer">
            Mêmes horaires toute la semaine
          </Label>
          <Checkbox
            id="same-hours"
            checked={sameHoursAllWeek}
            onCheckedChange={setSameHoursAllWeek}
            data-testid="checkbox-same-hours"
          />
        </div>

        {sameHoursAllWeek ? (
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <Label className="text-xs text-slate-500 mb-1 block">Ouverture</Label>
              <Input type="time" defaultValue="09:00" className="h-11 rounded-lg" data-testid="input-open-all" />
            </div>
            <span className="text-slate-400 mt-5">→</span>
            <div className="flex-1">
              <Label className="text-xs text-slate-500 mb-1 block">Fermeture</Label>
              <Input type="time" defaultValue="19:00" className="h-11 rounded-lg" data-testid="input-close-all" />
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {DAYS.map((day) => (
              <div key={day.id} className="flex items-center gap-3">
                <div className="w-24 flex items-center gap-2">
                  <Checkbox
                    checked={closedDays.includes(day.id)}
                    onCheckedChange={() => toggleClosedDay(day.id)}
                    data-testid={`checkbox-closed-${day.id}`}
                  />
                  <span className={`text-sm ${closedDays.includes(day.id) ? 'text-slate-400 line-through' : 'text-slate-700 font-medium'}`}>
                    {day.label}
                  </span>
                </div>
                {!closedDays.includes(day.id) && (
                  <>
                    <Input 
                      type="time" 
                      defaultValue="09:00" 
                      className="flex-1 h-10 rounded-lg text-sm" 
                      data-testid={`input-open-${day.id}`}
                    />
                    <span className="text-slate-400 text-sm">→</span>
                    <Input 
                      type="time" 
                      defaultValue="19:00" 
                      className="flex-1 h-10 rounded-lg text-sm"
                      data-testid={`input-close-${day.id}`}
                    />
                  </>
                )}
                {closedDays.includes(day.id) && (
                  <span className="text-sm text-red-500 italic">Fermé</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Moyens de paiement */}
      <div className="bg-white rounded-2xl border border-slate-100 p-6 space-y-4">
        <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-emerald-500" />
          Moyens de paiement acceptés au comptoir
          <span className="text-red-500">*</span>
        </h2>
        <p className="text-sm text-slate-500">Sélectionnez au moins un moyen de paiement</p>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {PAYMENT_METHODS.map((method) => {
            const isSelected = selectedPayments.includes(method.id);
            return (
              <button
                key={method.id}
                onClick={() => togglePayment(method.id)}
                className={`flex items-center gap-3 p-3 border-2 rounded-xl transition-all ${
                  isSelected 
                    ? 'border-emerald-500 bg-emerald-50' 
                    : 'border-slate-200 hover:border-slate-300'
                }`}
                data-testid={`payment-${method.id}`}
              >
                {typeof method.icon === 'string' ? (
                  <span className="text-xl">{method.icon}</span>
                ) : (
                  <method.icon className="w-5 h-5 text-slate-600" />
                )}
                <span className={`text-sm font-medium ${isSelected ? 'text-emerald-700' : 'text-slate-700'}`}>
                  {method.label}
                </span>
                {isSelected && <Check className="w-4 h-4 text-emerald-500 ml-auto" />}
              </button>
            );
          })}
        </div>
      </div>

      {/* Intégration Stripe */}
      <div className="bg-white rounded-2xl border border-slate-100 p-6 space-y-4">
        <h2 className="text-lg font-semibold text-slate-900">
          Intégration paiement en ligne
          <span className="text-slate-400 font-normal text-sm ml-2">(optionnel)</span>
        </h2>
        <p className="text-sm text-slate-500">
          Connectez votre compte Stripe pour accepter les paiements en ligne
        </p>
        <Button variant="outline" className="h-12 rounded-xl" data-testid="btn-connect-stripe">
          <img src="https://upload.wikimedia.org/wikipedia/commons/b/ba/Stripe_Logo%2C_revised_2016.svg" alt="Stripe" className="h-5 mr-2" />
          Connecter Stripe
        </Button>
      </div>
    </div>
  );
}

// === ÉTAPE 6: Compte responsable ===
function AccountStep() {
  return (
    <div className="space-y-6" data-testid="step-account">
      <div className="text-center">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
          Compte responsable
        </h1>
        <p className="text-slate-500">
          Informations du responsable principal du commerce
        </p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-6 space-y-5">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm font-medium text-slate-700">
              Prénom <span className="text-red-500">*</span>
            </Label>
            <Input
              type="text"
              placeholder="Jean"
              className="mt-1.5 h-12 rounded-xl"
              data-testid="input-firstname"
            />
          </div>
          <div>
            <Label className="text-sm font-medium text-slate-700">
              Nom <span className="text-red-500">*</span>
            </Label>
            <Input
              type="text"
              placeholder="Dupont"
              className="mt-1.5 h-12 rounded-xl"
              data-testid="input-lastname"
            />
          </div>
        </div>

        <div>
          <Label className="text-sm font-medium text-slate-700">
            Email personnel <span className="text-red-500">*</span>
          </Label>
          <div className="relative mt-1.5">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              type="email"
              placeholder="jean.dupont@email.com"
              className="h-12 rounded-xl pl-10"
              data-testid="input-personal-email"
            />
          </div>
        </div>

        <div>
          <Label className="text-sm font-medium text-slate-700">
            Téléphone personnel <span className="text-red-500">*</span>
          </Label>
          <div className="relative mt-1.5">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              type="tel"
              placeholder="+33 6 12 34 56 78"
              className="h-12 rounded-xl pl-10"
              data-testid="input-personal-phone"
            />
          </div>
        </div>

        <div>
          <Label className="text-sm font-medium text-slate-700">
            Rôle dans l'entreprise <span className="text-red-500">*</span>
          </Label>
          <select 
            className="mt-1.5 w-full h-12 rounded-xl border border-slate-200 px-4 bg-white focus:border-emerald-500 focus:ring-emerald-500"
            data-testid="select-role"
          >
            <option value="">Sélectionnez un rôle</option>
            <option value="owner">Propriétaire</option>
            <option value="manager">Gérant</option>
            <option value="staff_lead">Responsable d'équipe</option>
            <option value="other">Autre</option>
          </select>
        </div>

        <div>
          <Label className="text-sm font-medium text-slate-700">
            Mot de passe <span className="text-red-500">*</span>
          </Label>
          <Input
            type="password"
            placeholder="Minimum 8 caractères"
            className="mt-1.5 h-12 rounded-xl"
            data-testid="input-password"
          />
          <p className="text-xs text-slate-500 mt-1.5">
            Au moins 8 caractères, une majuscule et un chiffre
          </p>
        </div>

        <div>
          <Label className="text-sm font-medium text-slate-700">
            Confirmer le mot de passe <span className="text-red-500">*</span>
          </Label>
          <Input
            type="password"
            placeholder="••••••••"
            className="mt-1.5 h-12 rounded-xl"
            data-testid="input-confirm-password"
          />
        </div>
      </div>
    </div>
  );
}

// === ÉTAPE 7: Documents ===
function DocumentsStep() {
  return (
    <div className="space-y-6" data-testid="step-documents">
      <div className="text-center">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
          Documents & conformité
        </h1>
        <p className="text-slate-500">
          Téléchargez les documents nécessaires pour votre activité
        </p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-6 space-y-5">
        {/* Licence alcool */}
        <div>
          <Label className="text-sm font-medium text-slate-700 flex items-center gap-2">
            <Wine className="w-4 h-4 text-purple-500" />
            Licence de vente d'alcool
            <span className="text-slate-400 font-normal">(si applicable)</span>
          </Label>
          <div className="mt-2 border-2 border-dashed border-slate-200 rounded-xl p-6 text-center hover:border-purple-300 transition-colors cursor-pointer" data-testid="upload-alcohol-license">
            <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
            <p className="text-sm text-slate-600">Télécharger la licence</p>
            <p className="text-xs text-slate-400 mt-1">PDF, PNG, JPG</p>
          </div>
        </div>

        {/* Autres documents */}
        <div>
          <Label className="text-sm font-medium text-slate-700">
            Autres documents
            <span className="text-slate-400 font-normal ml-2">(optionnel)</span>
          </Label>
          <div className="mt-2 border-2 border-dashed border-slate-200 rounded-xl p-6 text-center hover:border-slate-300 transition-colors cursor-pointer" data-testid="upload-other">
            <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
            <p className="text-sm text-slate-600">Ajouter d'autres documents</p>
            <p className="text-xs text-slate-400 mt-1">Kbis, attestation d'assurance, etc.</p>
          </div>
        </div>
      </div>

      <div className="bg-emerald-50 rounded-xl p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-emerald-700">
          Vous pourrez créer votre menu/carte directement depuis votre dashboard après l'inscription.
        </p>
      </div>

      <div className="bg-amber-50 rounded-xl p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-amber-700">
          Vous pouvez passer cette étape et ajouter les documents plus tard depuis votre espace.
        </p>
      </div>
    </div>
  );
}

// === ÉTAPE 8: Confirmation ===
function ConfirmationStep({ shopType }) {
  const shopTypeLabel = SHOP_TYPES.find(t => t.id === shopType)?.label || 'Commerce';
  
  return (
    <div className="space-y-6" data-testid="step-confirmation">
      <div className="text-center">
        <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="w-8 h-8 text-emerald-600" />
        </div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
          Récapitulatif
        </h1>
        <p className="text-slate-500">
          Vérifiez vos informations avant de finaliser
        </p>
      </div>

      {/* Récapitulatif par section */}
      <div className="space-y-4">
        {/* Type de commerce */}
        <SummaryCard 
          title="Type de commerce" 
          icon={Building2}
          items={[{ label: 'Type', value: shopTypeLabel }]}
        />

        {/* Informations de base */}
        <SummaryCard 
          title="Informations de base" 
          icon={MapPin}
          items={[
            { label: 'Nom', value: 'Boulangerie du Marché' },
            { label: 'Adresse', value: '15 Rue de la Paix, 75001 Paris' },
            { label: 'Téléphone', value: '+33 1 23 45 67 89' },
            { label: 'Email', value: 'contact@boulangerie-marche.fr' },
          ]}
        />

        {/* Opérations */}
        <SummaryCard 
          title="Détails opérationnels" 
          icon={Clock}
          items={[
            { label: 'Horaires', value: '06:00 - 20:00 (Lun-Sam)' },
            { label: 'Paiements', value: 'Carte, Espèces' },
          ]}
        />

        {/* Compte */}
        <SummaryCard 
          title="Responsable" 
          icon={User}
          items={[
            { label: 'Nom', value: 'Jean Dupont' },
            { label: 'Rôle', value: 'Propriétaire' },
            { label: 'Email', value: 'jean.dupont@email.com' },
          ]}
        />
      </div>

      {/* Checkbox de validation */}
      <div className="bg-white rounded-2xl border border-slate-100 p-6">
        <div className="flex items-start gap-3">
          <Checkbox id="confirm" className="mt-1" data-testid="checkbox-confirm" />
          <label htmlFor="confirm" className="text-sm text-slate-600 cursor-pointer">
            J'ai vérifié que toutes les informations sont correctes et j'accepte les{' '}
            <a href="#" className="text-emerald-600 hover:underline">conditions générales d'utilisation</a>
            {' '}et la{' '}
            <a href="#" className="text-emerald-600 hover:underline">politique de confidentialité</a>.
          </label>
        </div>
      </div>

      {/* Session d'onboarding */}
      <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl border border-emerald-100 p-6 space-y-4">
        <h3 className="font-semibold text-slate-900 flex items-center gap-2">
          <Calendar className="w-5 h-5 text-emerald-600" />
          Session d'accompagnement (optionnel)
        </h3>
        <p className="text-sm text-slate-600">
          Besoin d'aide pour démarrer ? Planifiez un appel avec notre équipe.
        </p>
        <div className="flex flex-wrap gap-3">
          <Button variant="outline" className="h-10 rounded-lg" data-testid="btn-call">
            <Phone className="w-4 h-4 mr-2" />
            Appel téléphonique
          </Button>
          <Button variant="outline" className="h-10 rounded-lg" data-testid="btn-whatsapp">
            <MessageCircle className="w-4 h-4 mr-2" />
            WhatsApp
          </Button>
          <Button variant="outline" className="h-10 rounded-lg" data-testid="btn-video">
            <Video className="w-4 h-4 mr-2" />
            Visio
          </Button>
        </div>
        <Button variant="ghost" className="text-sm text-slate-500 h-auto p-0 hover:text-slate-700" data-testid="btn-skip-onboarding">
          Passer cette étape →
        </Button>
      </div>
    </div>
  );
}

// Composant carte récapitulatif
function SummaryCard({ title, icon: Icon, items }) {
  return (
    <div className="bg-white rounded-xl border border-slate-100 p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-slate-900 flex items-center gap-2">
          <Icon className="w-4 h-4 text-emerald-500" />
          {title}
        </h3>
        <Button variant="ghost" size="sm" className="text-emerald-600 h-auto p-0 text-sm hover:text-emerald-700">
          Modifier
        </Button>
      </div>
      <div className="space-y-2">
        {items.map((item, index) => (
          <div key={index} className="flex justify-between text-sm">
            <span className="text-slate-500">{item.label}</span>
            <span className="text-slate-700 font-medium">{item.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
